import * as React from 'react';
import { ChevronRight } from 'lucide-react';

export interface AvailabilityLoadMoreAffordanceProps {
  /** Rendered as the compact header label (e.g. "Więcej"). Also used as the cell `title`. */
  label: string;
  isLoading: boolean;
  onLoadMore: () => void;
  /** Renders the header variant (icon above label) instead of the per-row cell variant. */
  variant: 'header' | 'cell';
}

/**
 * Trailing "load more periods" affordance for the inventory catalog `DataTable`. Rendered
 * as a real appended column (see `buildLoadMoreColumn`) so it sits at the true right edge
 * of the availability columns and scrolls into view exactly when the user reaches the end
 * of the loaded timeline — no sticky/absolute positioning needed. Every row's cell repeats
 * the same clickable chevron with identical styling, reading as one continuous vertical bar
 * spanning the table height.
 */
export function AvailabilityLoadMoreAffordance({
  label,
  isLoading,
  onLoadMore,
  variant,
}: AvailabilityLoadMoreAffordanceProps) {
  const activate = () => {
    if (!isLoading) onLoadMore();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    activate();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      activate();
    }
  };

  if (variant === 'header') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="w-full h-full flex flex-col items-center justify-center gap-0.5 py-1.5 text-2xs font-medium text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
        title={label}
        data-testid="load-more-periods-header"
      >
        <ChevronRight className="h-3.5 w-3.5" />
        {label}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`w-full h-full flex items-center justify-center bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors ${
        isLoading ? 'opacity-40 pointer-events-none' : ''
      }`}
      title={label}
      data-testid="load-more-periods-cell"
    >
      <ChevronRight
        className={`h-3.5 w-3.5 text-muted-foreground ${isLoading ? 'animate-pulse' : ''}`}
      />
    </div>
  );
}
